import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { verifyToken } from '@/lib/jwtUtils';
import api from '@/lib/api';
import { User } from '@/types';

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  isFirstTimeLogin: boolean;
  login: (userData: User, isFirstTime?: boolean) => void;
  logout: () => void;
  completeFirstTimeLogin: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isFirstTimeLogin, setIsFirstTimeLogin] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Check if user is logged in
    const validateAndSetAuth = async () => {
      console.log('AuthContext: Validating authentication...');
      const token = localStorage.getItem('token');
      const currentPath = location.pathname;

      if (token) {
        try {
          // Verify token is valid and not expired
          const payload = verifyToken(token);

          if (payload) {
            console.log('AuthContext: Token is valid, fetching user profile');

            // Fetch user profile to ensure user still exists and is active
            const response = await api.get('/auth/me');
            const userData = response.data;

            // Create user object from API response
            const user = {
              id: userData.id || payload.sub || payload.userId,
              username: userData.email || payload.username,
              name: userData.name,
              email: userData.email,
              role: userData.roles?.includes('ROLE_ADMIN') ? 'ADMIN' : 'USER',
              collegeId: userData.tenantId
            };

            setUser(user);

            // Check first-time login status
            const isFirstTime = userData.isFirstTimeLogin || localStorage.getItem('isFirstTimeLogin') === 'true';
            setIsFirstTimeLogin(isFirstTime);
            console.log('AuthContext: First-time login status:', isFirstTime);

            // If this is a first-time login, redirect to password reset
            if (isFirstTime && currentPath !== '/first-time-password-reset') {
              console.log('AuthContext: First-time login detected, redirecting to password reset');
              navigate('/first-time-password-reset', { replace: true });
              setIsLoading(false);
              return;
            }

            // If we're on the login page, redirect to dashboard
            if (currentPath === '/login') {
              console.log('AuthContext: Already logged in, redirecting to dashboard');
              setTimeout(() => {
                navigate('/', { replace: true });
              }, 100);
            }

            setIsLoading(false);
            return;
          } else {
            console.error('AuthContext: Token is expired or invalid');

            // Clear the invalid token
            localStorage.removeItem('token');
            setUser(null);

            // If we're not on a public page, redirect to login
            const publicRoutes = ['/login', '/reset-password', '/first-time-password-reset'];
            if (!publicRoutes.includes(currentPath)) {
              console.log('AuthContext: Token invalid, redirecting to login');
              navigate('/login', { replace: true });
            }

            setIsLoading(false);
            return;
          }
        } catch (error) {
          console.error('AuthContext: Token validation error:', error);
          localStorage.removeItem('token');
          setUser(null);

          // Show user-friendly error message for service unavailability
          if (!(error as any).response) {
            console.error('AuthContext: Authentication service unavailable');
            // Don't redirect to login if service is down, let user stay on current page
            // They'll see the error when they try to use authenticated features
          }
        }
      } else {
        console.log('AuthContext: No token found');
      }

      setIsLoading(false);
    };

    validateAndSetAuth();
  }, [location.pathname, navigate]);

  const login = (userData: User, isFirstTime: boolean = false) => {
    console.log('AuthContext: Setting user data:', userData, 'isFirstTime:', isFirstTime);
    setUser(userData);
    setIsFirstTimeLogin(isFirstTime);

    // Store first-time login flag in localStorage
    if (isFirstTime) {
      localStorage.setItem('isFirstTimeLogin', 'true');
    } else {
      localStorage.setItem('isFirstTimeLogin', 'false');
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('tenantCode'); // Clear tenant code on logout
    localStorage.removeItem('isFirstTimeLogin'); // Clear first-time login flag
    setUser(null);
    setIsFirstTimeLogin(false);
    navigate('/login');
  };

  const completeFirstTimeLogin = () => {
    setIsFirstTimeLogin(false);
    localStorage.setItem('isFirstTimeLogin', 'false');
    console.log('AuthContext: First-time login completed, redirecting to dashboard');
    navigate('/', { replace: true });
  };

  return (
    <AuthContext.Provider value={{
      user,
      isAuthenticated: !!user,
      isLoading,
      isFirstTimeLogin,
      login,
      logout,
      completeFirstTimeLogin
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
